import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import moment from 'moment';
import { Checkbox, IconButton } from '@cbrebuild/blocks';
import SortableList from '../ConsiderationTab/SortableListLocal';
import Avatar from '../../../nucleus/avatar/avatar';
import Tooltip from '../../../nucleus/tooltip/tooltip';
import SimpleConfirmationModal from '../../modals/SimpleConfirmationModal';
import VoucherRecipientAutocomplete from '../../autocomplete/voucher-recipient-autocomplete';
import dealsService from '../../../services/deals-service';
import { fetchAttachments } from '../../../services/attachments-service';
import vouchersService from '../../../services/voucher/vouchers-service';
import {
  formatCustomEscalationRow,
  formatLeaseEscalation,
  getCustomEscalationRows,
  isFixedEscalationValue,
} from '../../notifications/voucher-verification-notification';
import { toast } from '../../../redux/actions/toasts-actions';
import {
  addRecipientExclusion,
  buildCommissionRowIdMap,
  buildDealMemberRowIdMap,
  canonicalizeRecipientsWithDealMembers,
  deduplicateRecipients,
  getRecipientIdentityKeys,
  isRecipientExcluded,
  mergeBrokerTeamRecipients,
  normalizeDealTeamMembers,
  removeRecipientExclusion,
  recipientsMatch,
  reconcileCommissionRecipients,
  resolveRecipientGreetingName,
  updateAutoRecipientGreeting,
} from './recipient-utils';
import './voucher-verification.scss';
import { FIELD_ID_TO_BE_NAME, getLeaseDuration, getOutlierFieldIds } from '../../../utils/broker-verification-outliers';

const sortAlpha = (a, b) => a.label.localeCompare(b.label);

const formatTimestamp = (timestamp) => {
  if (timestamp == null || (typeof timestamp === 'string' && !timestamp.trim())) return '—';
  const date = new Date(timestamp);
  if (Number.isNaN(date.getTime())) return '—';
  const dateText = date.toLocaleDateString('en-US', { month: 'numeric', day: 'numeric', year: 'numeric' });
  const timeText = date.toLocaleTimeString('en-US', {
    hour: 'numeric', minute: '2-digit', hour12: true, timeZoneName: 'short',
  });
  return `${dateText} at ${timeText}`;
};

const getSubmitterName = (submitter) => {
  if (typeof submitter === 'string') return submitter.trim();
  return submitter?.name
    || [submitter?.first_name, submitter?.last_name].filter(Boolean).join(' ')
    || submitter?.email || '';
};

const EMPTY_AUDIT_VALUE_PLACEHOLDERS = new Set(['', '—', '-']);

const hasAuditValue = value => value !== null
  && value !== undefined
  && !EMPTY_AUDIT_VALUE_PLACEHOLDERS.has(String(value).trim());

const getCommissionAgreementAuditAnswer = (changes) => {
  const answerChange = [...changes].reverse().find(change => (
    change.field_name === 'commission_agreement'
    || change.field_name === 'has_commission_agreement'
  ));
  const answerValue = answerChange?.new_value ?? answerChange?.old_value;
  if (typeof answerValue === 'boolean') return answerValue;
  const normalizedValue = String(answerValue ?? '').trim().toLowerCase();
  if (normalizedValue === 'true' || normalizedValue === 'yes') return true;
  if (normalizedValue === 'false' || normalizedValue === 'no') return false;
  return null;
};

const getAuditFieldLabel = (change, commissionAgreementAnswer) => {
  if (change.field_name === 'commission_agreement' || change.field_name === 'has_commission_agreement') {
    return 'Is there a commission agreement?';
  }
  if (change.field_name === 'attachments') {
    return commissionAgreementAnswer === false ? 'Supporting Attachments' : 'Commission Agreement Attachments';
  }
  if (!/^lease escalation(?:\s+\((?:custom|fixed)\))?$/i.test(String(change.field_label || '').trim())) {
    return change.field_label;
  }

  for (const value of [change.new_value, change.old_value]) {
    if (getCustomEscalationRows(value)) return 'Lease Escalation (Custom)';
    if (isFixedEscalationValue(value)) return 'Lease Escalation (Fixed)';
    const strVal = String(value).trim().toLowerCase();
    if (strVal === 'true') return 'Lease Escalation (Custom)';
    if (strVal === 'false') return 'Lease Escalation (Fixed)';
  }

  return change.field_label;
};

const getEscalationMode = (value) => {
  if (getCustomEscalationRows(value)) return 'Custom';
  if (isFixedEscalationValue(value)) return 'Fixed';
  if (/^(true|false)$/i.test(String(value).trim())) {
    return String(value).trim().toLowerCase() === 'true' ? 'Custom' : 'Fixed';
  }
  return null;
};

const getCommissionAgreementAttachmentNames = (attachments, documentCategory = 'CA') => attachments
  .filter(attachment => attachment.document_category === documentCategory)
    .map((attachment) => {
      const name = attachment.new_name || attachment.document_name || attachment.filename || attachment.title;
      return typeof name === 'string' ? name.trim() : '';
    })
    .filter(Boolean);

// free_rent audit values arrive as a stringified {years, months} dict (or a plain
// total-months number for older entries) -- render both consistently as "Y year(s) M month(s)".
const formatYearsMonths = (years, months) => {
  const yearsPart = `${years} year${years !== 1 ? 's' : ''}`;
  if (!months) return yearsPart;
  return `${yearsPart} ${months} month${months !== 1 ? 's' : ''}`;
};

const formatFreeRentAuditValue = (value) => {
  const str = String(value).trim();

  if (typeof value === 'object' && value !== null) {
    const years = Math.trunc(Number(value.years ?? 0)) || 0;
    const months = Math.trunc(Number(value.months ?? 0)) || 0;
    return formatYearsMonths(years, months);
  }

  const yearMatch = str.match(/years?['"]?\s*:\s*(-?\d+)/i);
  const monthMatch = str.match(/months?['"]?\s*:\s*(-?\d+)/i);
  if (yearMatch || monthMatch) {
    const years = Number(yearMatch?.[1] || 0);
    const months = Number(monthMatch?.[1] || 0);
    return formatYearsMonths(years, months);
  }

  if (/^-?\d+(\.\d+)?$/.test(str)) {
    const totalMonths = Math.round(Number(str));
    const years = Math.trunc(totalMonths / 12);
    const months = totalMonths % 12;
    return formatYearsMonths(years, months);
  }

  return str;
};

// Date-typed audit fields -- old_value can arrive pre-formatted (e.g. "Aug 29, 2028")
// while new_value is ISO ("2030-08-27"), so both need normalizing to the same mm/dd/yyyy display.
const AUDIT_DATE_FIELD_NAMES = new Set([
  'date_lease_execution',
  'date_lease_from',
  'date_lease_occupancy',
  'lease_expiration_date',
]);

const formatAuditDateValue = (stringValue) => {
  // ISO 'YYYY-MM-DD' -- read the parts directly rather than through Date() to avoid any
  // local-timezone shift.
  const isoMatch = stringValue.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (isoMatch) return `${isoMatch[2]}/${isoMatch[3]}/${isoMatch[1]}`;

  const parsed = new Date(stringValue);
  if (Number.isNaN(parsed.getTime())) return stringValue;
  const mm = String(parsed.getMonth() + 1).padStart(2, '0');
  const dd = String(parsed.getDate()).padStart(2, '0');
  return `${mm}/${dd}/${parsed.getFullYear()}`;
};

const formatAuditValue = (fieldName, value) => {
  if (!hasAuditValue(value)) return '—';

  const stringValue = String(value).trim();
  if (fieldName === 'free_rent') return formatFreeRentAuditValue(value);
  if (fieldName === 'fully_executed_lease' || fieldName === 'commission_agreement') {
    if (/^true$/i.test(stringValue)) return 'Yes';
    if (/^false$/i.test(stringValue)) return 'No';
  }
  if (AUDIT_DATE_FIELD_NAMES.has(fieldName)) return formatAuditDateValue(stringValue);

  const dateMatch = stringValue.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  return dateMatch ? `${dateMatch[2]}/${dateMatch[3]}/${dateMatch[1]}` : stringValue;
};

const AuditValue = ({ fieldName, fieldLabel, value }) => {
  const isEscalation = fieldName === 'rent_escalation_is_custom'
    || fieldName === 'lease_escalation'
    || /^lease escalation(?:\s|$)/i.test(String(fieldLabel || '').trim());
  if (isEscalation && isFixedEscalationValue(value)) {
    return <strong>{formatLeaseEscalation(value).replace(/^Fixed:\s*/, '')}</strong>;
  }
  const customRows = isEscalation ? getCustomEscalationRows(value) : null;
  if (customRows) {
    if (!customRows.length) return <strong>No rent rows</strong>;
    return (
      <ul className="vv-verified-escalation-list">
        {customRows.map((row, index) => (
          <li key={`rent-${index}`}>{formatCustomEscalationRow(row, index)}</li>
        ))}
      </ul>
    );
  }

  const attachmentNames = fieldName === 'attachments'
    ? (value?.length ? value : ['None Selected'])
    : null;

  if (!attachmentNames) return <strong>{formatAuditValue(fieldName, value)}</strong>;

  return (
    <ul className="vv-verified-attachment-list">
      {attachmentNames.map((name, index) => (
        <li key={`${name}-${index}`}><strong>{name}</strong></li>
      ))}
    </ul>
  );
};

// Required fields — always shown, always checked, cannot be reordered
const DEFAULT_FIELD_DEFINITIONS = {
  tenant_name: { id: 'tenant_name', label: 'Tenant Name' },
  tenant_rep_company: { id: 'tenant_rep_company', label: 'Tenant Rep Company' },
  landlord_name: { id: 'landlord_name', label: 'Landlord Name' },
  landlord_rep_company: { id: 'landlord_rep_company', label: 'Landlord Rep Company' },
};

const getDefaultFields = (repRole) => {
  const tenantFields = [
    DEFAULT_FIELD_DEFINITIONS.tenant_name,
    DEFAULT_FIELD_DEFINITIONS.tenant_rep_company,
  ];
  const landlordFields = [
    DEFAULT_FIELD_DEFINITIONS.landlord_name,
    DEFAULT_FIELD_DEFINITIONS.landlord_rep_company,
  ];
  return repRole === 'landlord'
    ? [...landlordFields, ...tenantFields]
    : [...tenantFields, ...landlordFields];
};

// Fixed optional fields remain first; the remaining fields are sorted alphabetically.
const FIXED_OPTIONAL_FIELDS = [
  {
    id: 'is_fully_executed_lease',
    label: 'Is this a fully executed lease?',
    checked: true,
    isOob: false,
    isFixed: true,
    field_position_id: 5,
    field_value: '—',
  },
  {
    id: 'has_commission_agreement',
    label: 'Is there a commission agreement?',
    checked: true,
    isOob: false,
    isFixed: true,
    field_position_id: 6,
    field_value: '—',
  },
];

const INITIAL_OPTIONAL_FIELDS = [
  ...FIXED_OPTIONAL_FIELDS,
  ...[
  { id: 'base_rent',                      label: 'Base Rent',                             checked: false, isOob: false },
  { id: 'end_date',                       label: 'Lease Expiration Date',                 checked: false, isOob: false },
  { id: 'floor_number',                   label: 'Floor',                                 checked: false, isOob: false },
  { id: 'free_rent',                      label: 'Free Rent',                             checked: false, isOob: false },
  { id: 'lease_escalation',               label: 'Lease Escalation',                      checked: false, isOob: false },
  { id: 'occupancy_date',                 label: 'Lease Occupancy Date',                  checked: false, isOob: false },
  { id: 'operating_expenses',             label: 'Operating Expenses ($/SqFt per year)',  checked: false, isOob: false },
  { id: 'lease_execution_date',           label: 'Lease Execution Date',                  checked: false, isOob: false },
  { id: 'start_date',                     label: 'Lease Start Date',                      checked: false, isOob: false },
    {
      id: 'space_total_square_feet', label: 'Square Feet', checked: false, isOob: false,
    },
    {
      id: 'building_total_square_feet', label: 'Square Footage', checked: false, isOob: false,
    },
  { id: 'tenant_improvement_allowance',   label: 'Tenant Improvement Allowance ($/SqFt)', checked: false, isOob: false },
  { id: 'term',                           label: 'Lease Term (months)',                   checked: false, isOob: false },
].sort(sortAlpha),
];

// rep_role === 'landlord' means the deal's client is the landlord and counter_party is the tenant.
const getFieldBeName = (fieldId, repRole) => {
  if (fieldId === 'tenant_name') return repRole === 'landlord' ? 'counter_party_name' : 'client_name';
  if (fieldId === 'landlord_name') return repRole === 'landlord' ? 'client_name' : 'counter_party_name';
  return FIELD_ID_TO_BE_NAME[fieldId] || fieldId;
};

const renderRecipientAvatar = (recipient) => {
  const image = recipient.avatar || recipient.scraped_avatar || recipient.user_uploaded_avatar;
  if (image) {
    return <Avatar image={image} firstName={recipient.first_name} lastName={recipient.last_name} />;
  } else {
    return <Avatar firstName={recipient.first_name} lastName={recipient.last_name} />;
  }
};

const DEFAULT_EMAIL_BODY = `Hi [Recipient Name],\n\n A voucher is ready for your review.\nPlease click below to verify the details and complete this step.\n\n Thanks, \n\n [Sender Name]`;

const SUBMITTED_VOUCHER_STATUSES = [
  'Voucher Submitted',
  'Voucher Resubmitted',
  'Voucher Completed',
];
const REMOVED_RECIPIENTS_STORAGE_VERSION = 'v2';
const SOURCE_KEYS_STORAGE_VERSION = 'v1';

// In-memory cache for within-session navigations only; cleared on page refresh
const verificationDraftCache = {};

const getStoredDraft = (voucherPk) => verificationDraftCache[voucherPk] || null;

const storeDraft = (voucherPk, draft) => {
  verificationDraftCache[voucherPk] = draft;
};

// Drag handle: three horizontal lines (reorder icon)
const DragHandleIcon = ({ color }) => (
  <svg width="12" height="10" viewBox="0 0 12 10" fill="none" aria-hidden="true">
    <line x1="0" y1="1" x2="12" y2="1" stroke={color} strokeWidth="1.5" strokeLinecap="round" />
    <line x1="0" y1="5" x2="12" y2="5" stroke={color} strokeWidth="1.5" strokeLinecap="round" />
    <line x1="0" y1="9" x2="12" y2="9" stroke={color} strokeWidth="1.5" strokeLinecap="round" />
  </svg>
);



const VoucherVerificationTab = () => {
  const {
    deal, voucher, userData, brokerBounds, commercialProperties,
  } = useSelector(state => ({
    deal: state.deal.deal,
    voucher: state.voucher.voucher,
    userData: state.userData?.data || {},
    brokerBounds: state.voucher.brokerVerificationBounds,
    commercialProperties: state.voucher.commercialProperties,
  }));

  const [optionalFields, setOptionalFields] = useState(INITIAL_OPTIONAL_FIELDS);
  const [emailBody, setEmailBody] = useState(DEFAULT_EMAIL_BODY);
  const [draftId, setDraftId] = useState(null);
  const [draftLoaded, setDraftLoaded] = useState(false);
  const draftIdRef = useRef(null);
  const draftGetPromiseRef = useRef(null);
  const verificationBoundsRef = useRef(null);
  const resolvedCompRef = useRef({});
  const resolvedInfoRef = useRef({});
  const commissionInfoRef = useRef(null);
  const currentUserRef = useRef(userData);
  const dealMembersRef = useRef([]);
  const removedRecipientKeysRef = useRef([]);
  // tracks which commission brokers / deal members were present on the last run, so a
  // removal from the source (not just from the recipient list) clears its exclusion
  const prevCommissionBrokerKeysRef = useRef(new Set());
  const prevDealMemberKeysRef = useRef(new Set());
  // last-known assignment row id per identity, so a remove-then-re-add of the same person
  // (new row id) is detected even across an unmount/remount (e.g. switching tabs) where the
  // in-between absence itself was never observed by this component
  const commissionRowIdsRef = useRef({});
  const dealMemberRowIdsRef = useRef({});
  currentUserRef.current = userData;
  // true when draft had no field_config (new deal); cleared after initial bounds save
  const isNewDraftRef = useRef(false);
  const hasSavedInitialBoundsRef = useRef(false);
  // prevents re-adding the lead producer once already handled in this mount
  const leadProducerSentRef = useRef(false);
  // prevents re-adding current user as recipient on re-renders once already saved
  const currentUserSentRef = useRef(false);
  const fieldConfigLoadedRef = useRef(false);
  const defaultLayoutSyncedRef = useRef(false);
  // debounce timers so rapid successive state changes on load coalesce into a single PATCH
  const fieldConfigSaveTimeoutRef = useRef(null);
  const recipientsSaveTimeoutRef = useRef(null);
  const includeExcelScrollRef = useRef({ x: 0, y: 0 });
  // Same fix as includeExcelScrollRef, reused for the locked/pre-checked fixed + OOB
  // checkboxes below -- clicking a disabled-onChange Checkbox still lets the browser focus
  // and scrollIntoView its underlying input, jumping the page.
  const lockedFieldScrollRef = useRef({ x: 0, y: 0 });
  const autoRecipientGreetingNameRef = useRef(null);

  const [verificationBounds, setVerificationBounds] = useState(null);

  // Increments each time the user returns to this tab; drives bounds re-check on re-activation
  const [tabActivation, setTabActivation] = useState(0);
  const prevPathnameRef = useRef(null);

  const [showMoreInstructions, setShowMoreInstructions] = useState(true);
  const [includeExcel, setIncludeExcel] = useState(true);
  const [recipients, setRecipients] = useState([]);
  const [removedRecipientKeys, setRemovedRecipientKeys] = useState([]);
  const [submittedDate, setSubmittedDate] = useState(null);
  const [submittedBy, setSubmittedBy] = useState('');
  const [dealMembers, setDealMembers] = useState([]);
  const [dealMembersLoaded, setDealMembersLoaded] = useState(false);
  const [auditLog, setAuditLog] = useState([]);
  const [dealAttachments, setDealAttachments] = useState([]);
  const [verificationStatus, setVerificationStatus] = useState(null);
  const [isSendingEmail, setIsSendingEmail] = useState(false);
  const [showResendConfirmation, setShowResendConfirmation] = useState(false);
  const [verifiedAt, setVerifiedAt] = useState(null);
  const lastSentTime = submittedDate ? moment(submittedDate).valueOf() : NaN;
  const hasSentVerificationEmail = Number.isFinite(lastSentTime);

  const history = useHistory();
  const location = useLocation();
  const dispatch = useDispatch();
  const voucherId = voucher?.deal;

  // Re-increment tabActivation whenever the user navigates back to this tab
  useEffect(() => {
    const here = location.pathname.includes('voucher_verification');
    const wasElsewhere = prevPathnameRef.current !== null && !prevPathnameRef.current.includes('voucher_verification');
    if (here && wasElsewhere) setTabActivation(n => n + 1);
    prevPathnameRef.current = location.pathname;
  }, [location.pathname]); // eslint-disable-line react-hooks/exhaustive-deps
  const dealId = deal?.id || voucher?.deal;
  const removedRecipientsStorageKey = dealId
    ? `voucher-verification:${dealId}:removed-recipients:${REMOVED_RECIPIENTS_STORAGE_VERSION}`
    : null;
  // Persisted so the source-removal check below survives a browser refresh, not just re-renders
  const commissionBrokerKeysStorageKey = dealId
    ? `voucher-verification:${dealId}:commission-broker-keys:${SOURCE_KEYS_STORAGE_VERSION}`
    : null;
  const dealMemberKeysStorageKey = dealId
    ? `voucher-verification:${dealId}:deal-member-keys:${SOURCE_KEYS_STORAGE_VERSION}`
    : null;
  const commissionRowIdsStorageKey = dealId
    ? `voucher-verification:${dealId}:commission-row-ids:${SOURCE_KEYS_STORAGE_VERSION}`
    : null;
  const dealMemberRowIdsStorageKey = dealId
    ? `voucher-verification:${dealId}:deal-member-row-ids:${SOURCE_KEYS_STORAGE_VERSION}`
    : null;

  const persistRemovedRecipientKeys = (keys) => {
    const uniqueKeys = [...new Set(keys)];
    removedRecipientKeysRef.current = uniqueKeys;
    setRemovedRecipientKeys(uniqueKeys);
    if (!removedRecipientsStorageKey) return;
    try {
      localStorage.setItem(removedRecipientsStorageKey, JSON.stringify(uniqueKeys));
    } catch (error) {
      // Recipient removal still applies for the current session when storage is unavailable.
    }
  };

  // Records the current source membership (ref + localStorage) so it survives a refresh
  const persistSourceKeys = (ref, storageKey, keysSet) => {
    ref.current = keysSet;
    if (!storageKey) return;
    try {
      localStorage.setItem(storageKey, JSON.stringify([...keysSet]));
    } catch (error) {
      // Falls back to in-memory tracking for this session when storage is unavailable.
    }
  };

  const loadSourceKeys = (storageKey) => {
    if (!storageKey) return new Set();
    try {
      const stored = JSON.parse(localStorage.getItem(storageKey) || '[]');
      return new Set(Array.isArray(stored) ? stored : []);
    } catch (error) {
      return new Set();
    }
  };

  // Records the current identity -> assignment-row-id mapping (ref + localStorage) so it
  // survives an unmount (tab switch) as well as a refresh
  const persistRowIdMap = (ref, storageKey, map) => {
    ref.current = map;
    if (!storageKey) return;
    try {
      localStorage.setItem(storageKey, JSON.stringify(map));
    } catch (error) {
      // Falls back to in-memory tracking for this session when storage is unavailable.
    }
  };

  const loadRowIdMap = (storageKey) => {
    if (!storageKey) return {};
    try {
      const stored = JSON.parse(localStorage.getItem(storageKey) || '{}');
      return stored && typeof stored === 'object' && !Array.isArray(stored) ? stored : {};
    } catch (error) {
      return {};
    }
  };

  // If someone previously removed as a recipient is later dropped from the source
  // (commissions / deal team), clear their exclusion so re-adding them to the source
  // later makes them reappear as a recipient instead of staying permanently hidden.
  const clearExclusionsForRemovedSource = (prevKeys, currentKeys) => {
    const goneFromSource = [...prevKeys].filter(key => !currentKeys.has(key));
    if (goneFromSource.length === 0) return;
    const goneSet = new Set(goneFromSource);
    const stillExcluded = removedRecipientKeysRef.current.filter(key => goneSet.has(key));
    if (stillExcluded.length === 0) return;
    persistRemovedRecipientKeys(
      removedRecipientKeysRef.current.filter(key => !goneSet.has(key)),
    );
  };

  // Same intent as above, but catches a remove-then-re-add that happened entirely while this
  // component was unmounted (e.g. the user edited commissions/team on another tab): the person
  // is present both before and after, but their assignment row id changed in between.
  const clearExclusionsForReassignedSource = (prevRowIds, currentRowIds) => {
    const reassignedKeys = Object.keys(currentRowIds).filter(key => (
      prevRowIds[key] != null && currentRowIds[key] != null && prevRowIds[key] !== currentRowIds[key]
    ));
    if (reassignedKeys.length === 0) return;
    const reassignedSet = new Set(reassignedKeys);
    const stillExcluded = removedRecipientKeysRef.current.filter(key => reassignedSet.has(key));
    if (stillExcluded.length === 0) return;
    persistRemovedRecipientKeys(
      removedRecipientKeysRef.current.filter(key => !reassignedSet.has(key)),
    );
  };

  useEffect(() => {
    if (!removedRecipientsStorageKey) return;
    try {
      localStorage.removeItem(`voucher-verification:${dealId}:removed-recipients`);
      const storedKeys = JSON.parse(localStorage.getItem(removedRecipientsStorageKey) || '[]');
      persistRemovedRecipientKeys(Array.isArray(storedKeys) ? storedKeys : []);
    } catch (error) {
      persistRemovedRecipientKeys([]);
    }
    prevCommissionBrokerKeysRef.current = loadSourceKeys(commissionBrokerKeysStorageKey);
    prevDealMemberKeysRef.current = loadSourceKeys(dealMemberKeysStorageKey);
    commissionRowIdsRef.current = loadRowIdMap(commissionRowIdsStorageKey);
    dealMemberRowIdsRef.current = loadRowIdMap(dealMemberRowIdsStorageKey);
  }, [dealId, removedRecipientsStorageKey, commissionBrokerKeysStorageKey, dealMemberKeysStorageKey, commissionRowIdsStorageKey, dealMemberRowIdsStorageKey]); // eslint-disable-line react-hooks/exhaustive-deps

  // Cancel any pending debounced saves so they don't fire PATCH calls after unmount
  useEffect(() => () => {
    clearTimeout(fieldConfigSaveTimeoutRef.current);
    clearTimeout(recipientsSaveTimeoutRef.current);
  }, []);

  // Fetched data from /mbb/deals/{id}/deal_overview/
  const [dealOverview, setDealOverview] = useState(null);
  // Fetched from /mbb/voucher/commercialproperty/ so area fields don't depend on another page
  // having populated Redux first.
  const [fetchedCommercialProperties, setFetchedCommercialProperties] = useState(null);
  const verificationInfo = useMemo(() => {
    // deal_overview's deal_info is refetched on every tab activation, so it is the primary source;
    // the commercialproperty API / Redux copies are fallbacks until it has loaded.
    let source = commercialProperties || [];
    if (fetchedCommercialProperties?.length) source = fetchedCommercialProperties;
    if (dealOverview !== null) {
      source = dealOverview?.deal_info?.voucher_commercial_properties || [];
    }
    return {
      ...(dealOverview?.deal_info || {}),
      // Exclude the tenant's prior location; whole_building may arrive as a "true"/"false" string.
      voucher_commercial_properties: source
        .filter(p => p.location_type_new !== false)
        .map(p => ({ ...p, whole_building: p.whole_building === true || p.whole_building === 'true' })),
    };
  }, [dealOverview, commercialProperties, fetchedCommercialProperties]);
  resolvedInfoRef.current = verificationInfo;
  const areaProperties = verificationInfo.voucher_commercial_properties;
  const isFieldApplicable = (field) => {
    if (field.id === 'space_total_square_feet') return areaProperties.some(property => !property.whole_building);
    if (field.id === 'building_total_square_feet') return areaProperties.some(property => property.whole_building);
    return true;
  };
  const isFullyExecutedLeaseAnswer = dealOverview?.deal_info?.is_fully_executed_lease ?? null;
  const hasCommissionAgreementAnswer = dealOverview?.deal_info?.has_commission_agreement ?? null;

  // Same property-specific bounds the Review page and the "is verification needed" decision use,
  // so Base Rent is judged identically everywhere. Our own fetch below is only a fallback.
  const sharedBounds = brokerBounds?.voucherId === voucher?.id && brokerBounds?.status === 'ready'
    ? brokerBounds.data
    : null;
  const boundsFromSharedRef = useRef(false);
  const commercialPropertiesRef = useRef(commercialProperties);
  commercialPropertiesRef.current = commercialProperties;
  useEffect(() => {
    if (!sharedBounds) return;
    boundsFromSharedRef.current = true;
    verificationBoundsRef.current = sharedBounds;
    setVerificationBounds(sharedBounds);
  }, [sharedBounds]);

  useEffect(() => {
    if (!dealId) return;
    dealsService.fetchDealOverview(dealId)
      .then((data) => {
        setDealOverview(data);
        resolvedCompRef.current = data?.comp_info?.results?.[0] || {};
        commissionInfoRef.current = data?.commission_info?.results || [];
        const normalizedDealMembers = normalizeDealTeamMembers(data?.team_info?.results || []);
        dealMembersRef.current = normalizedDealMembers;
        setDealMembers(normalizedDealMembers);
        setDealMembersLoaded(true);
      })
      .catch(() => setDealMembersLoaded(true));
    Promise.resolve(vouchersService.fetchCommercialProperty(dealId))
      .then(data => setFetchedCommercialProperties(Array.isArray(data) ? data : (data?.results || [])))
      .catch(() => {});
    if (boundsFromSharedRef.current) return;
    const primaryProperty = (commercialPropertiesRef.current || []).find(p => p.location_type_new !== false);
    vouchersService.fetchBrokerVerificationBounds(
      primaryProperty?.edp_property_id ? { edp_property_id: primaryProperty.edp_property_id } : undefined,
    )
      .then((data) => {
        if (boundsFromSharedRef.current) return;
        verificationBoundsRef.current = data;
        setVerificationBounds(data);
      })
      .catch(() => {});
  }, [dealId, tabActivation]);

  useEffect(() => {
    if (!dealId) {
      setDealAttachments([]);
      return undefined;
    }

    let isCurrent = true;
    fetchAttachments({ deal: dealId, ordering: '-modified', page: 1, page_size: 25 })
      .then((data) => {
        const attachments = Array.isArray(data) ? data : data?.results;
        if (isCurrent) setDealAttachments(attachments || []);
      })
      .catch(() => {
        if (isCurrent) setDealAttachments([]);
      });
    return () => { isCurrent = false; };
  }, [dealId]);

  // Once either question has actually been answered, move it out of the locked/checked
  // "fixed" group into the ordinary valid-fields list -- unchecked and pushed to the bottom
  // (a fresh end-of-list position, same as an out-of-bounds field returning to the valid
  // list once resolved, above) -- and let its real Yes/No answer show up in the preview
  // instead of the hardcoded '—' placeholder.
  useEffect(() => {
    const commissionAgreementAttachmentNames = getCommissionAgreementAttachmentNames(dealAttachments);
    const answerByFieldId = {
      is_fully_executed_lease: isFullyExecutedLeaseAnswer,
      has_commission_agreement: hasCommissionAgreementAnswer === true
        && !commissionAgreementAttachmentNames.length
        ? null
        : hasCommissionAgreementAnswer,
    };
    if (Object.values(answerByFieldId).every(v => v == null)) return;
    setOptionalFields((prev) => {
      const maxValidPos = prev
        .filter(f => !f.isFixed)
        .reduce((m, f) => Math.max(m, f.field_position_id || 0), getDefaultFields((dealOverview?.deal_info || deal)?.rep_role).length);
      let nextPos = maxValidPos + 1;
      let changed = false;
      const updated = prev.map((field) => {
        const answer = answerByFieldId[field.id];
        if (!field.isFixed || answer == null) return field;
        changed = true;
        return {
          ...field,
          isFixed: false,
          checked: false,
          field_value: answer ? 'Yes' : 'No',
          field_position_id: nextPos++,
        };
      });
      if (!changed) return prev;
      saveFieldConfig(updated);
      return updated;
    });
  }, [isFullyExecutedLeaseAnswer, hasCommissionAgreementAnswer, dealAttachments, dealOverview, deal, draftLoaded]); // eslint-disable-line react-hooks/exhaustive-deps

  // GET /verification_draft/ — skipped if we already fetched this draft (prevents re-creation on re-mount)
  useEffect(() => {
    const voucherPk = voucher?.id;
    if (!voucherPk) return;

    const applyDraft = (draft) => {
      setDraftLoaded(false);
      if (draft?.id) {
        setDraftId(draft.id);
        draftIdRef.current = draft.id;
      }
      if (draft?.email_body) setEmailBody(draft.email_body);
      if (draft?.include_attachment != null) setIncludeExcel(draft.include_attachment);
      const sentAt = draft?.passed_to_queue_at || draft?.submitted_date;
      setSubmittedDate(sentAt || null);
      setSubmittedBy(getSubmitterName(draft?.submitted_by));
      if (draft?.verification_status) setVerificationStatus(draft.verification_status);
      if (draft?.verification_status === 'Verified') setShowMoreInstructions(false);
      if (draft?.verified_date) setVerifiedAt(draft.verified_date);
      if (Array.isArray(draft?.audit)) setAuditLog(draft.audit);

      if (Array.isArray(draft?.recipients) && draft.recipients.length) {
        const loaded = deduplicateRecipients(draft.recipients).map(r => ({
          id: r.user_id || r.email,
          user_id: r.user_id || null,
          first_name: r.first_name || '',
          last_name: r.last_name || '',
          work_email: r.email || '',
          removable: true,
          broker_is_lead: r.broker_is_lead || false,
          is_broker: r.is_broker || false,
          avatar: r.avatar || r.scraped_avatar || r.user_uploaded_avatar || '',
        })).filter(recipient => !isRecipientExcluded(recipient, removedRecipientKeysRef.current));
        setRecipients((prev) => {
          const merged = canonicalizeRecipientsWithDealMembers(
            [...loaded, ...prev],
            dealMembersRef.current,
          );
          return commissionInfoRef.current === null
            ? merged
            : canonicalizeRecipientsWithDealMembers(
              reconcileCommissionRecipients(
                merged,
                commissionInfoRef.current,
                [currentUserRef.current],
                removedRecipientKeysRef.current,
              ),
              dealMembersRef.current,
            );
        });
        // Block auto-add of LP and commission brokers if draft already contains them
        // This allows newly assigned LPs/brokers to still be added when they change
        if (loaded.some(r => r.broker_is_lead)) leadProducerSentRef.current = true;
        currentUserSentRef.current = true;
      }

      const config = draft?.deal_field_layout;
      if (!config || !config.length) {
        isNewDraftRef.current = true;
        // No saved config to restore, but the check is done — treat as loaded so later
        // bounds re-checks trust local `checked` state instead of resetting it every time.
        fieldConfigLoadedRef.current = true;
        setDraftLoaded(true);
        return;
      }

      const repRoleForDefaults = (dealOverview?.deal_info || deal)?.rep_role;
      const defaultFields = getDefaultFields(repRoleForDefaults);
      const defaultIds = new Set([
        ...defaultFields.map(f => f.id),
        ...defaultFields.map(f => getFieldBeName(f.id, repRoleForDefaults)).filter(Boolean),
      ]);
      const optionalEntries = [...config]
        .sort((a, b) => a.field_position_id - b.field_position_id)
        .filter(c => !defaultIds.has(c.field_name));

      // Legacy drafts saved before Lease Escalation was consolidated into a single field may
      // still carry separate rent_escalation_dollar/rent_escalation_percent entries (or the
      // current rent_escalation_is_custom entry) — merge them into one so an old draft's
      // checked/position state carries over instead of resetting to unchecked.
      const escalationFieldNames = new Set(['rent_escalation_dollar', 'rent_escalation_percent', 'rent_escalation_is_custom']);
      const legacyEscalationEntries = optionalEntries.filter(e => escalationFieldNames.has(e.field_name));
      const dedupedOptionalEntries = optionalEntries.filter(e => !escalationFieldNames.has(e.field_name));
      if (legacyEscalationEntries.length) {
        dedupedOptionalEntries.push(legacyEscalationEntries.find(e => e.is_included) || legacyEscalationEntries[0]);
      }

      setOptionalFields((prev) => {
        const bounds = verificationBoundsRef.current;
        // On remount, bounds may not be loaded yet; fall back to cached OOB IDs so isOob is accurate
        const oob = bounds
          ? getOutlierFieldIds(bounds, resolvedCompRef.current, resolvedInfoRef.current, voucher)
          : draft.oob_field_ids ? new Set(draft.oob_field_ids) : new Set();
        const fromApi = dedupedOptionalEntries
          .map(entry => {
            const base = prev.find(f => FIELD_ID_TO_BE_NAME[f.id] === entry.field_name
              || f.id === entry.field_name
              || (f.id === 'lease_escalation' && escalationFieldNames.has(entry.field_name)));
            if (!base) return null;
            return {
              ...base,
              field_position_id: base.isFixed ? base.field_position_id : entry.field_position_id,
              checked: base.isFixed || oob.has(base.id),
              isOob: oob.has(base.id),
              field_value: entry.field_value ?? base.field_value,
            };
          })
          .filter(Boolean);
        const apiIds = new Set(fromApi.map(f => f.id));
        const remaining = prev.filter(f => !apiIds.has(f.id)).map(f => ({
          ...f,
          checked: f.isFixed ? true : f.checked,
          isOob: oob.has(f.id),
        }));
        fieldConfigLoadedRef.current = true;
        return [...fromApi, ...remaining].sort((a, b) => {
          if (a.field_position_id && b.field_position_id) return a.field_position_id - b.field_position_id;
          if (a.field_position_id) return -1;
          if (b.field_position_id) return 1;
          return a.label.localeCompare(b.label);
        });
      });
      setDraftLoaded(true);
    };

    const cached = getStoredDraft(deal?.id);
    if (cached) {
      applyDraft(cached);
    }

    draftGetPromiseRef.current = vouchersService.getVerificationDraft(deal?.id)
      .then((draft) => {
        storeDraft(deal?.id, draft);
        applyDraft(draft);
      })
      .catch(() => {});
  }, [voucher?.id]);

  // Re-check bounds without overwriting the saved field configuration.
  useEffect(() => {
    if (!verificationBounds || !dealOverview) return;
    const comp = dealOverview?.comp_info?.results?.[0] || {};
    const oob = getOutlierFieldIds(verificationBounds, comp, verificationInfo, voucher);
    // Persist current OOB field IDs to cache so re-mount restores isOob accurately
    const dealIDForOob = deal?.id;
    const draftForOob = dealIDForOob ? getStoredDraft(dealIDForOob) : null;
    if (draftForOob) storeDraft(dealIDForOob, { ...draftForOob, oob_field_ids: [...oob] });
    setOptionalFields((prev) => {
      const fixed = prev.filter(f => f.isFixed);
      // Max position ID among fields that are currently valid and will stay valid after this update
      const stableValidMaxPos = prev
        .filter(f => !f.isFixed && !f.isOob && !oob.has(f.id))
        .reduce((m, f) => Math.max(m, f.field_position_id || 0), getDefaultFields(resolvedDeal.rep_role).length + fixed.length);
      let nextPos = stableValidMaxPos + 1;

      const updated = prev.map(field => {
        const nowOob = oob.has(field.id);
        // Only reposition on a real post-load transition; before the config loads, bounds may
        // still arrive in multiple partial passes and shouldn't break the alphabetical default order.
        const transitioningToValid = fieldConfigLoadedRef.current && field.isOob && !nowOob && !field.isFixed;
        return {
          ...field,
          // If a field was OOB but is now in-bounds, uncheck it (it was auto-checked by OOB, not by user)
          checked: nowOob
            ? true
            : (!fieldConfigLoadedRef.current || transitioningToValid ? false : field.checked),
          isOob: nowOob,
          // Assign a new end position so the returning field doesn't collide with renumbered valid fields
          ...(transitioningToValid ? { field_position_id: nextPos++ } : {}),
        };
      });

      // Detect any OOB, checked, or position change so we persist even on re-mount (tabActivation resets to 0)
      const hasFieldChange = fieldConfigLoadedRef.current && updated.some(f => {
        const prevField = prev.find(p => p.id === f.id);
        return prevField && (
          f.checked !== prevField.checked ||
          f.isOob !== prevField.isOob ||
          f.field_position_id !== prevField.field_position_id
        );
      });
      if (isNewDraftRef.current && !hasSavedInitialBoundsRef.current) {
        hasSavedInitialBoundsRef.current = true;
        saveFieldConfig(updated);
      } else if (fieldConfigLoadedRef.current && (tabActivation > 0 || hasFieldChange)) {
        // Don't persist until the saved field config has loaded, or a stale default `checked`
        // computed above (before restore) would overwrite the user's saved selection.
        saveFieldConfig(updated);
      }
      return updated;
    });
  }, [verificationBounds, dealOverview, verificationInfo, draftId, tabActivation, voucher]); // eslint-disable-line react-hooks/exhaustive-deps

  // Resolved deal info from deal_overview response
  const resolvedDeal = dealOverview?.deal_info || deal || {};
  const resolvedComp = dealOverview?.comp_info?.results?.[0] || {};
  const leadProducer = (() => {
    const commissions = dealOverview?.commission_info?.results || [];
    const lead = commissions.find(c => c.broker_is_lead && c.payment_group === 1 && c.broker);
    return lead?.broker || null;
  })();

  // LP entry from recipients state — drives the LP row display and removal
  const displayedRecipients = canonicalizeRecipientsWithDealMembers(recipients, dealMembers);
  const lpRecipient = displayedRecipients.find(r => r.broker_is_lead) || null;
  const defaultFields = useMemo(() => getDefaultFields(resolvedDeal.rep_role), [resolvedDeal.rep_role]);
  const dealName = resolvedDeal.name || deal?.name || '—';

  // Rewrite existing drafts once when the role-aware default order differs from
  // the saved deal_field_layout positions.
  useEffect(() => {
    if (!draftId || !fieldConfigLoadedRef.current || defaultLayoutSyncedRef.current) return;
    const savedLayout = getStoredDraft(dealId)?.deal_field_layout;
    if (!Array.isArray(savedLayout) || !savedLayout.length) return;

    const defaultLayoutMatches = defaultFields.every((field, index) => {
      const expectedFieldName = getFieldBeName(field.id, resolvedDeal.rep_role);
      const savedField = savedLayout.find(entry => entry.field_name === expectedFieldName);
      return savedField?.field_position_id === index + 1;
    });

    defaultLayoutSyncedRef.current = true;
    if (!defaultLayoutMatches) saveFieldConfig(optionalFields);
  }, [draftId, dealId, defaultFields, resolvedDeal.rep_role, optionalFields]); // eslint-disable-line react-hooks/exhaustive-deps

  const tenantParty = resolvedDeal.rep_role === 'landlord' ? resolvedDeal.counter_party : resolvedDeal.client;
  const landlordParty = resolvedDeal.rep_role === 'landlord' ? resolvedDeal.client : resolvedDeal.counter_party;
  const property = resolvedDeal.voucher_commercial_properties?.[0];
  const propertyAddress = property || {};
  // Address on first line
  const propertyAddressLine = propertyAddress.address || '';
  // City, state, zip on second line
  const propertyCityStateLine = [
    propertyAddress.city || '',
    propertyAddress.state_abbreviation || '',
    propertyAddress.postal_code || '',
  ].filter(Boolean).join(', ');

  const formatCurrency = val => (val != null ? `$${Number(val).toFixed(2)}` : '—');
  // Backend decimals arrive as strings like "12277.000"; show them as "12277".
  const formatSquareFeet = (val) => {
    if (val == null || String(val).trim() === '' || Number.isNaN(Number(val))) return '—';
    return Number(val).toString();
  };
  const formatDate = val => (val ? moment.parseZone(val).format('MMM D, YYYY') : '—');
  // Lease Escalation's checklist/preview label switches between "(Fixed)" and "(Custom)"
  // based on the same rent_escalation_is_custom selection made on the Lease Comp tab, or
  // shows no suffix at all when that selection hasn't been made yet.
  const getFieldLabel = (field) => {
    if (field.id === 'lease_escalation') {
      if (resolvedComp.rent_escalation_is_custom == null) return 'Lease Escalation';
      return `Lease Escalation (${resolvedComp.rent_escalation_is_custom ? 'Custom' : 'Fixed'})`;
    }
    return field.label;
  };

  // The deal_field_layout payload stores Lease Escalation's raw underlying data (not the
  // human-readable preview string) so the backend can render the correct Fixed/Custom
  // editable form on the verifier page: '' when unset, a Fixed {dollar, percent, is_percent}
  // object, or the raw Custom schedule row array.
  const getFieldConfigValue = (field) => {
    if (field.id === 'lease_escalation') {
      if (resolvedComp.rent_escalation_is_custom == null) return '';
      if (resolvedComp.rent_escalation_is_custom) return resolvedComp.rent_escalation_custom || [];
      return {
        rent_escalation_dollar: resolvedComp.rent_escalation_dollar,
        rent_escalation_percent: resolvedComp.rent_escalation_percent,
        rent_escalation_is_percent: resolvedComp.rent_escalation_is_percent,
      };
    }
    return getPreviewValue(field.id);
  };

  // Shared by the table preview (columns) and the plain-text fallback (joined string) so
  // both stay in sync. Year/Month are derived from the same total-months value Lease Comp's
  // breakdown uses; a rent value or months that hasn't actually been entered shows [Blank].
  const getEscalationRowData = (row, index) => {
    const isPercent = row?.is_percent === true || String(row?.is_percent).toLowerCase() === 'true';
    const rawValue = isPercent ? row?.rent_percent : row?.rent_dollar;
    const value = rawValue != null && rawValue !== ''
      ? (isPercent ? `${rawValue}%` : formatCurrency(rawValue))
      : '[Blank]';
    const hasMonths = row?.months != null && row?.months !== '';
    const years = hasMonths ? Math.floor(row.months / 12) : '[Blank]';
    const months = hasMonths ? row.months % 12 : '[Blank]';
    return { rowNumber: index + 1, value, years, months };
  };

  const formatEscalationScheduleRow = (row, index) => {
    const { rowNumber, value, years, months } = getEscalationRowData(row, index);
    return `Rent ${rowNumber} : ${value} Year : ${years} Month : ${months}`;
  };

  // Build preview rows from deal_overview (deal_info + comp_info)
  const getPreviewValue = (fieldId) => {
    switch (fieldId) {
      // DEFAULT_FIELDS
      case 'tenant_name':          return tenantParty?.name || '—';
      case 'tenant_rep_company':   return resolvedComp.tenant_represented_by || '—';
      case 'landlord_name':        return landlordParty?.name || '—';
      case 'landlord_rep_company': return resolvedComp.landlord_represented_by || '—';
      // INITIAL_OPTIONAL_FIELDS
      case 'base_rent':                    return formatCurrency(resolvedComp.base_rent);
      case 'end_date':                     return formatDate(voucher?.lease_expiration_date);
      case 'floor_number':                 return resolvedDeal.voucher_commercial_properties?.[0]?.spaces?.[0]?.floor ?? '—';
      case 'space_total_square_feet':
        return areaProperties.filter(commercialProperty => !commercialProperty.whole_building)
          .flatMap(commercialProperty => (commercialProperty.spaces || []).map(space => formatSquareFeet(space.space_total_square_feet)))
          .join(', ') || '—';
      case 'building_total_square_feet':
        return areaProperties.filter(commercialProperty => commercialProperty.whole_building)
          .map(commercialProperty => formatSquareFeet(commercialProperty.building_total_square_feet)).join(', ') || '—';
      case 'free_rent':                    return resolvedComp.free_rent != null ? String(resolvedComp.free_rent) : '—';
      case 'lease_escalation': {
        if (resolvedComp.rent_escalation_is_custom) {
          const rows = resolvedComp.rent_escalation_custom || [];
          return rows.length ? rows.map(formatEscalationScheduleRow).join('\n') : '—';
        }
        return resolvedComp.rent_escalation_is_percent
          ? (resolvedComp.rent_escalation_percent != null ? `${resolvedComp.rent_escalation_percent}%` : '—')
          : formatCurrency(resolvedComp.rent_escalation_dollar);
      }
      case 'occupancy_date':               return formatDate(resolvedDeal.date_lease_occupancy);
      case 'operating_expenses':           return formatCurrency(resolvedComp.operating_expenses_amount_per_sqft);
      case 'lease_execution_date':         return formatDate(resolvedDeal.date_lease_execution);
      case 'start_date':                   return formatDate(resolvedDeal.date_lease_from);
      case 'tenant_improvement_allowance': return formatCurrency(resolvedComp.tenant_improvement_allowance_amount_per_sqft);
      case 'term': {
        const leaseTermMonths = getLeaseDuration(resolvedDeal.date_lease_from, voucher?.lease_expiration_date);
        return leaseTermMonths != null ? String(leaseTermMonths) : '—';
      }
      case 'is_fully_executed_lease':      return isFullyExecutedLeaseAnswer != null ? (isFullyExecutedLeaseAnswer ? 'Yes' : 'No') : '—';
      case 'has_commission_agreement': {
        const answer = hasCommissionAgreementAnswer;
        if (answer == null) return '—';
        const attachmentNames = getCommissionAgreementAttachmentNames(
          dealAttachments,
          answer ? 'CA' : 'OTH',
        );
        return attachmentNames.length
          ? `${answer ? 'Yes' : 'No'}\n${attachmentNames.map(name => `\u2022 ${name}`).join('\n')}`
          : (answer ? 'Yes  (Document Needed)' : 'No');
      }
      default: return '—';
    }
  };

  const senderFirstName = userData?.first_name || '';
  const senderLastInitial = userData?.last_name ? userData.last_name[0] : '';
  const senderDisplay = senderFirstName
    ? `${senderFirstName}${senderLastInitial ? ` ${senderLastInitial}.` : ''}`
    : 'Your Name';

  // Lead producer takes precedence; otherwise use the single broker (commission or team
  // fallback) if there's exactly one -- ambiguous (0 or 2+) means no name is populated.
  const recipientGreetingName = resolveRecipientGreetingName(lpRecipient, displayedRecipients);

  // Keep the generated greeting aligned with the live recipient roles.
  useEffect(() => {
    if (!draftLoaded || !draftId || !dealOverview || !dealMembersLoaded) return;
    const brokerGreetingName = displayedRecipients
      .filter(recipient => !recipient.broker_is_lead && recipient.is_broker)
      .map(recipient => recipient.first_name || recipient.name || '')
      .find(name => name && emailBody.startsWith(`Hi ${name},`));
    let updated = updateAutoRecipientGreeting(
      emailBody,
      autoRecipientGreetingNameRef.current || brokerGreetingName,
      recipientGreetingName,
    );
    autoRecipientGreetingNameRef.current = recipientGreetingName;
    if (senderDisplay !== 'Your Name') {
      updated = updated.replace('[Sender Name]', senderDisplay);
    }
    if (updated === emailBody) return;
    setEmailBody(updated);
    saveEmailBody(updated);
  }, [draftLoaded, draftId, dealOverview, dealMembersLoaded, emailBody, recipientGreetingName, senderDisplay]); // eslint-disable-line react-hooks/exhaustive-deps

  // Rebuild commission roles from the latest deal overview.
  useEffect(() => {
    if (!draftId) return;
    const commissions = dealOverview?.commission_info?.results;
    if (!Array.isArray(commissions)) return;
    const currentBrokerKeys = new Set(
      commissions
        .filter(commission => commission.broker?.id || commission.broker?.user_id)
        .flatMap(commission => getRecipientIdentityKeys({
          user_id: commission.broker.user_id || commission.broker.id,
          work_email: commission.broker.work_email,
        })),
    );
    clearExclusionsForRemovedSource(prevCommissionBrokerKeysRef.current, currentBrokerKeys);
    persistSourceKeys(prevCommissionBrokerKeysRef, commissionBrokerKeysStorageKey, currentBrokerKeys);

    const currentCommissionRowIds = buildCommissionRowIdMap(commissions);
    clearExclusionsForReassignedSource(commissionRowIdsRef.current, currentCommissionRowIds);
    persistRowIdMap(commissionRowIdsRef, commissionRowIdsStorageKey, {
      ...commissionRowIdsRef.current,
      ...currentCommissionRowIds,
    });

    setRecipients((prev) => {
      const nextRecipients = canonicalizeRecipientsWithDealMembers(
        reconcileCommissionRecipients(
        prev,
        commissions,
        [userData],
        removedRecipientKeys,
        ),
        dealMembers,
      );
      if (nextRecipients.some(recipient => recipient.broker_is_lead)) {
        leadProducerSentRef.current = true;
      }
      return nextRecipients;
    });
  }, [dealOverview?.commission_info, draftId, userData, removedRecipientKeys]); // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-add current user to recipients once draft is ready
  // Ensures current user always appears (Scenario 1 & 2), deduplicated if already present
  useEffect(() => {
    if (!userData?.id || !draftId || currentUserSentRef.current) return;
    currentUserSentRef.current = true;
    const cuEntry = {
      id: userData.id,
      first_name: userData.first_name || '',
      last_name: userData.last_name || '',
      work_email: userData.work_email || '',
      avatar: userData.avatar || '',
      removable: true,
    };
    if (!isRecipientExcluded(cuEntry, removedRecipientKeysRef.current)) {
      setRecipients(prev => canonicalizeRecipientsWithDealMembers([...prev, cuEntry], dealMembers));
    }
  }, [userData?.id, draftId, dealMembers]); // eslint-disable-line react-hooks/exhaustive-deps

  // Team brokers are a fallback source only when the recipient list has never had a
  // lead producer. Once a lead is loaded or removed, keep that recipient choice intact.
  useEffect(() => {
    if (!draftId || !dealMembersLoaded) return;

    const currentMemberKeys = new Set(
      dealMembers.flatMap(member => getRecipientIdentityKeys({
        user_id: member.id,
        work_email: member.email?.work || member.work_email,
      })),
    );
    clearExclusionsForRemovedSource(prevDealMemberKeysRef.current, currentMemberKeys);
    persistSourceKeys(prevDealMemberKeysRef, dealMemberKeysStorageKey, currentMemberKeys);

    const currentDealMemberRowIds = buildDealMemberRowIdMap(dealOverview?.team_info?.results || []);
    clearExclusionsForReassignedSource(dealMemberRowIdsRef.current, currentDealMemberRowIds);
    persistRowIdMap(dealMemberRowIdsRef, dealMemberRowIdsStorageKey, {
      ...dealMemberRowIdsRef.current,
      ...currentDealMemberRowIds,
    });

    setRecipients(prev => mergeBrokerTeamRecipients(
      prev,
      dealMembers,
      !!lpRecipient || leadProducerSentRef.current,
      removedRecipientKeys,
    ));
  }, [draftId, dealMembersLoaded, dealMembers, lpRecipient?.id, removedRecipientKeys, dealOverview?.team_info]); // eslint-disable-line react-hooks/exhaustive-deps

  // Central persistence: fires after any recipients state change once the draft is ready
  useEffect(() => {
    if (!draftId) return;
    // Skip the empty initial state before any auto-population has started
    if (recipients.length === 0 && !leadProducerSentRef.current && !currentUserSentRef.current) return;
    persistRecipients(recipients);
  }, [recipients, draftId]); // eslint-disable-line react-hooks/exhaustive-deps


  // OOB fields: validation failed — pre-checked, locked, non-reorderable
  const sortByPosition = (a, b) =>
    (a.field_position_id || Number.MAX_SAFE_INTEGER) - (b.field_position_id || Number.MAX_SAFE_INTEGER);
  const applicableOptionalFields = optionalFields.filter(isFieldApplicable);
  const oobOptionalFields = applicableOptionalFields.filter(f => f.isOob).sort(sortByPosition);
  const fixedOptionalFields = applicableOptionalFields.filter(f => f.isFixed).sort(sortByPosition);
  // Valid fields: validation passed — reorderable, user-togglable
  const validOptionalFields = applicableOptionalFields.filter(f => !f.isOob && !f.isFixed).sort(sortByPosition);

  // Use the most recent verification audit when the response contains multiple entries.
  const verifyEntry = [...auditLog].reverse().find(e => e.action === 'verify' || e.action === 'reverify') || null;
  const verifierComment = verifyEntry?.details?.comments;
  const isVerified = verificationStatus === 'Verified' || verificationStatus === 'Reverified';

  // OOB fields precede valid fields in the payload so position IDs stay stable
  // Lease Escalation persists under a single constant backend field name regardless of
  // Fixed/Custom/unset — see getFieldConfigValue for how field_value shape varies by mode.
  const getOptionalFieldBeName = (field) => {
    if (field.id === 'lease_escalation') return 'rent_escalation_is_custom';
    return FIELD_ID_TO_BE_NAME[field.id] || field.id;
  };

  const buildFieldConfig = (fields) => {
    const applicableFields = fields.filter(isFieldApplicable);
    const fixed = applicableFields.filter(f => f.isFixed).sort((a, b) => a.field_position_id - b.field_position_id);
    const ordered = [
      ...applicableFields.filter(f => !f.isFixed && f.isOob).sort(sortByPosition),
      ...applicableFields.filter(f => !f.isFixed && !f.isOob).sort(sortByPosition),
    ];
    const optionalStartPosition = defaultFields.length;
    return [
      ...defaultFields.map((f, i) => ({
        field_name: getFieldBeName(f.id, resolvedDeal.rep_role),
        field_label: getFieldLabel(f),
        field_position_id: i + 1,
        is_included: true,
        is_editable: false,
        field_value: getFieldConfigValue(f),
      })),
      ...[...fixed, ...ordered].map((f, i) => ({
        field_name: getOptionalFieldBeName(f),
        field_label: getFieldLabel(f),
        field_position_id: optionalStartPosition + i + 1,
        is_included: f.isFixed ? true : f.checked,
        is_editable: f.id !== 'term',
        field_value: getFieldConfigValue(f),
      })),
    ];
  };

  const saveFieldConfig = (fields) => {
    const fieldConfig = buildFieldConfig(fields);
    const existingDraft = dealId && getStoredDraft(dealId);
    if (dealId && existingDraft) {
      storeDraft(dealId, { ...existingDraft, deal_field_layout: fieldConfig });
    }
    const doSave = (id) => {
      clearTimeout(fieldConfigSaveTimeoutRef.current);
      fieldConfigSaveTimeoutRef.current = setTimeout(() => {
        vouchersService.updateVerificationFieldConfig(id, fieldConfig).catch(() => {});
      }, 400);
    };
    if (draftIdRef.current) {
      doSave(draftIdRef.current);
      return;
    }
    const pending = draftGetPromiseRef.current;
    if (pending) {
      pending.then(() => {
        if (draftIdRef.current) doSave(draftIdRef.current);
      }).catch(() => {});
    }
  };

  useEffect(() => {
    const hasSavedArea = getStoredDraft(dealId)?.deal_field_layout?.some(field => (
      field.field_name === 'space_total_square_feet' || field.field_name === 'building_total_square_feet'
    ));
    if (draftLoaded && dealOverview && (areaProperties.length || hasSavedArea)) saveFieldConfig(optionalFields);
  }, [areaProperties, draftLoaded, dealOverview]); // eslint-disable-line react-hooks/exhaustive-deps

  const saveEmailBody = (value) => {
    const doSave = (id) => {
      vouchersService.updateVerificationEmailBody(id, value).catch(() => {});
    };
    if (draftIdRef.current) {
      doSave(draftIdRef.current);
      return;
    }
    const pending = draftGetPromiseRef.current;
    if (pending) {
      pending.then(() => {
        if (draftIdRef.current) doSave(draftIdRef.current);
      }).catch(() => {});
    }
  };

  // Persists the full deduped recipient list to the backend, replacing any previously stored list
  const persistRecipients = (list) => {
    if (!draftIdRef.current) return;
    const payload = deduplicateRecipients(list).map(r => ({
      user_id: r.user_id || (Number.isInteger(r.id) ? r.id : null),
      first_name: r.first_name || '',
      last_name: r.last_name || '',
      email: r.work_email || r.email || '',
      broker_is_lead: r.broker_is_lead || false,
      avatar: r.avatar || r.scraped_avatar || r.user_uploaded_avatar || '',
    }));
    // Keep cache in sync so re-navigation reads saved recipients and skips auto-add
    const existingDraft = dealId && getStoredDraft(dealId);
    if (dealId && existingDraft) {
      storeDraft(dealId, { ...existingDraft, recipients: payload });
    }
    // Debounced so the LP/current-user/broker-fallback auto-add effects on initial load
    // coalesce into a single PATCH instead of one per recipient added
    const draftId = draftIdRef.current;
    clearTimeout(recipientsSaveTimeoutRef.current);
    recipientsSaveTimeoutRef.current = setTimeout(() => {
      vouchersService.updateVerificationRecipients(draftId, payload).catch(() => {});
    }, 400);
  };

  const handleOrderChange = (oldIndex, newIndex) => {
    const fixed = optionalFields.filter(f => f.isFixed && isFieldApplicable(f));
    const oob = optionalFields.filter(f => f.isOob && isFieldApplicable(f));
    const valid = optionalFields.filter(f => !f.isOob && !f.isFixed && isFieldApplicable(f));
    const reordered = [...valid];
    const [moved] = reordered.splice(oldIndex, 1);
    reordered.splice(newIndex, 0, moved);
    const updated = [
      ...fixed,
      ...oob,
      ...optionalFields.filter(f => !isFieldApplicable(f)),
      ...reordered.map((field, index) => ({
        ...field,
        field_position_id: defaultFields.length + fixed.length + index + 1,
      })),
    ];
    setOptionalFields(updated);
    saveFieldConfig(updated);
  };

  const removeRecipient = (recipient) => {
    persistRemovedRecipientKeys(addRecipientExclusion(removedRecipientKeysRef.current, recipient));
    setRecipients(prev => prev.filter(existing => !recipientsMatch(existing, recipient)));
  };

  const restoreRecipient = (recipient) => {
    persistRemovedRecipientKeys(removeRecipientExclusion(removedRecipientKeysRef.current, recipient));
  };

  const addRecipient = (broker) => {
    if (!broker) return;

    if (typeof broker === 'string') {
      const typedEmail = broker.trim().toLowerCase();
      if (!typedEmail) return;
      // Check if already exists by email
      const alreadyExists = recipients.some(r => 
        (r.work_email || r.email || '').toLowerCase() === typedEmail
      );
      if (alreadyExists) return;
      
      const newEntry = {
        id: typedEmail,
        user_id: null,
        first_name: '',
        last_name: '',
        work_email: typedEmail,
        removable: true,
        avatar: '',
      };
      restoreRecipient(newEntry);
      setRecipients(prev => deduplicateRecipients([...prev, newEntry]));
      return;
    }

    const userId = broker.user_id || broker.id || broker.user?.id || null;
    const email = broker.work_email || broker.email || broker.user?.work_email || broker.user?.email || '';
    const id = userId || email;
    if (!id) return;

    const newEntry = {
      id,
      user_id: userId,
      first_name: broker.first_name || broker.given_name || '',
      last_name: broker.last_name || broker.surname || '',
      work_email: email,
      avatar: broker.avatar || '',
      removable: true,
    };
    restoreRecipient(newEntry);
    setRecipients(prev => deduplicateRecipients([...prev, newEntry]));
  };

  const filterSearchResults = recipient =>
    !recipients.some(r => recipientsMatch(r, {
      ...recipient,
      id: recipient.user_id || recipient.id || recipient.user?.id,
      work_email: recipient.work_email || recipient.email || recipient.user?.work_email || recipient.user?.email,
    }));

  const toggleField = (id) => {
    if (optionalFields.find(f => f.id === id)?.isOob) return;
    const updated = optionalFields.map(f => f.id === id ? { ...f, checked: !f.checked } : f);
    setOptionalFields(updated);
    saveFieldConfig(updated);
  };

  const handleSendVerificationEmail = () => {
    const draftId = draftIdRef.current;
    if (isSendingEmail) return;

    if (!deduplicateRecipients(recipients).length) {
      dispatch(toast('At least one recipient must be specified.'));
      return;
    }

    if (!emailBody?.trim()) {
      dispatch(toast('Email body cannot be empty.'));
      return;
    }

    if (!draftId || sendBlockReason) return;

    setIsSendingEmail(true);

    vouchersService.sendVerification(draftId, {})
      .then((response) => {
        dispatch(toast('Voucher Verification email is sent. Delivery to recipients can take up to three minutes.'));
        const passedToQueueAt = response?.submission_result?.passed_to_queue_at || new Date().toISOString();
        const submitter = response?.submitted_by || currentUserRef.current;
        const submitterId = response?.submitted_by_id ?? submitter?.id ?? null;
        setSubmittedDate(passedToQueueAt);
        setSubmittedBy(getSubmitterName(submitter));
        const existing = getStoredDraft(deal?.id);
        if (existing) storeDraft(deal?.id, {
          ...existing,
          passed_to_queue_at: passedToQueueAt,
          submitted_by: submitter,
          submitted_by_id: submitterId,
        });
      })
      .catch(() => {
        dispatch(toast('Failed to send Voucher Verification email. Please try again.'));
      })
      .finally(() => setIsSendingEmail(false));
  };

  const voucherSubmissionStatus = voucher?.voucher_submission_status || deal?.voucher_submission_status;
  const isVoucherSubmitted = SUBMITTED_VOUCHER_STATUSES.includes(voucherSubmissionStatus);
  const sendBlockReason = isVerified
    ? 'Voucher verification already completed. Please proceed to the "Send" screen.'
    : isVoucherSubmitted
      ? 'Voucher verification cannot proceed because this voucher has already been submitted.'
      : null;
  const isSendDisabled = !!sendBlockReason || isSendingEmail;
  const sendButton = (
    <button
      type="button"
      className={`vv-btn vv-btn--primary${isSendDisabled ? ' vv-btn--disabled' : ''}`}
      onClick={() => {
        if (hasSentVerificationEmail) setShowResendConfirmation(true);
        else handleSendVerificationEmail();
      }}
      disabled={isSendDisabled}
    >
      {hasSentVerificationEmail ? 'Resend Verification email' : 'Send Voucher Verification Email'}
    </button>
  );

  // Same fix as includeExcelScrollRef -- clicking a locked (checked, onChange no-op)
  // Checkbox still lets the browser focus + scrollIntoView its underlying input, jumping
  // the page. Recording the scroll position on mousedown and restoring it after the click
  // cancels that jump without touching the Checkbox's own look or behavior.
  const handleLockedFieldMouseDown = (event) => {
    lockedFieldScrollRef.current = { x: window.scrollX, y: window.scrollY };
    event.preventDefault();
  };
  const handleLockedFieldClick = () => {
    const { x, y } = lockedFieldScrollRef.current;
    const restoreScroll = () => window.scrollTo(x, y);
    window.requestAnimationFrame(() => {
      restoreScroll();
      window.setTimeout(restoreScroll, 0);
    });
  };

  return (
    <div className="voucher-verification-tab">
      {showResendConfirmation && (
        <SimpleConfirmationModal
          showModal={showResendConfirmation}
          onCancel={() => setShowResendConfirmation(false)}
          onAccept={() => {
            setShowResendConfirmation(false);
            handleSendVerificationEmail();
          }}
        >
          {`Verification email was last sent on ${formatTimestamp(submittedDate)}. Do you wish to resend?`}
        </SimpleConfirmationModal>
      )}
      {/* ── Section Header ── */}
      <div className="vv-section-header">
        <h2>Review Verification Email</h2>
        <div className="vv-instructions">
          <ol className="vv-instructions-text">
            <li>Confirm your selection of deal info &amp; terms on the left. Default and required fields cannot be removed or reordered.</li>
            {showMoreInstructions && (
              <>
                <li>Customize the Email Body and Preview the Draft (right side of screen).</li>
                <li>Choose recipients and send.</li>
              </>
            )}
          </ol>
          <button
            type="button"
            className="vv-show-more"
            onClick={() => setShowMoreInstructions(v => !v)}
          >
            {showMoreInstructions ? 'Show Less' : 'Show More'}
          </button>
        </div>

        {/* Shown inside the header card once draft status is 'verified' */}
        {isVerified && (
          <div className="vv-verified-banner">
            <div className="vv-verified-check" aria-hidden="true">
              <svg width="14" height="11" viewBox="0 0 14 11" fill="none">
                <path d="M1 5L5.5 9.5L13 1" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </div>
            <div className="vv-verified-content">
              <p className="vv-verified-title">
                Verification Completed on {formatTimestamp(verifiedAt)}
              </p>
              <p className="vv-verified-subtitle">Review recipients in the Send tab to continue.</p>
              {(() => {
                let visibleChanges = (verifyEntry?.changes || []).filter(change => (
                  change.field_name !== 'attachments'
                  || (change.old_attachment_names?.length > 0 || change.new_attachment_names?.length > 0)
                ));
                const commissionAgreementAnswer = getCommissionAgreementAuditAnswer(visibleChanges);
                const commissionAgreementIndex = visibleChanges.findIndex(change => (
                  change.field_name === 'commission_agreement'
                  || change.field_name === 'has_commission_agreement'
                ));
                const attachmentIndexes = visibleChanges.reduce((indexes, change, index) => {
                  if (change.field_name === 'attachments') indexes.push(index);
                  return indexes;
                }, []);
                if (commissionAgreementIndex >= 0 && attachmentIndexes.length) {
                  const groupedIndexes = new Set([commissionAgreementIndex, ...attachmentIndexes]);
                  const insertionIndex = Math.min(...groupedIndexes);
                  const groupedChanges = [
                    visibleChanges[commissionAgreementIndex],
                    ...attachmentIndexes.map(index => visibleChanges[index]),
                  ];
                  visibleChanges = visibleChanges.filter((change, index) => !groupedIndexes.has(index));
                  visibleChanges.splice(insertionIndex, 0, ...groupedChanges);
                }
                if (!(visibleChanges.length > 0 || verifierComment)) return null;
                return (
                <ul className="vv-verified-changes">
                  {visibleChanges.map((change, i) => {
                    const name = verifyEntry.verified_by?.name || 'Verifier';
                    const isEscalationModeFlag = (change.field_name === 'rent_escalation_is_custom'
                      || change.field_name === 'lease_escalation'
                      || /^lease escalation(?:\s|$)/i.test(String(change.field_label || '').trim()))
                      && /^(true|false)$/i.test(String(change.old_value).trim());
                    const isEscalation = change.field_name === 'rent_escalation_is_custom'
                      || change.field_name === 'lease_escalation'
                      || /^lease escalation(?:\s|$)/i.test(String(change.field_label || '').trim());
                    const oldEscalationMode = isEscalation ? getEscalationMode(change.old_value) : null;
                    const newEscalationMode = isEscalation ? getEscalationMode(change.new_value) : null;
                    const isEscalationModeTransition = oldEscalationMode && newEscalationMode
                      && oldEscalationMode !== newEscalationMode;
                    const transitionFieldLabel = String(change.field_label || 'Lease Escalation')
                      .replace(/\s+\((?:custom|fixed)\)$/i, '');
                    const isNewEscalationModeFlag = typeof change.new_value === 'boolean'
                      || /^(true|false)$/i.test(String(change.new_value ?? '').trim());
                    const hasOldValue = change.field_name === 'attachments'
                      ? change.old_attachment_names?.length > 0
                      : hasAuditValue(change.old_value) && !isEscalationModeFlag;
                    return (
                      <li key={i} className="vv-verified-change-item">
                        {isEscalationModeTransition ? (
                          <>
                            <strong>{name}</strong> changed the <strong>{transitionFieldLabel}</strong>
                            {' from '}<strong>{oldEscalationMode}</strong>{' to '}<strong>{newEscalationMode}</strong>
                            {isNewEscalationModeFlag ? '.' : (
                              <>
                                {': '}
                                <AuditValue
                                  fieldName={change.field_name}
                                  fieldLabel={change.field_label}
                                  value={change.new_value}
                                />{newEscalationMode === 'Custom' ? null : '.'}
                              </>
                            )}
                          </>
                        ) : hasOldValue ? (
                          <>
                            <strong>{name}</strong> changed the <strong>{getAuditFieldLabel(change, commissionAgreementAnswer)}</strong>
                            {getAuditFieldLabel(change, commissionAgreementAnswer) === 'Lease Escalation (Fixed)' ? ':' : ''}
                            {' '}{change.field_name === 'attachments' ? <div>from</div> : 'from '}<AuditValue
                              fieldName={change.field_name}
                              fieldLabel={change.field_label}
                              value={change.field_name === 'attachments'
                                ? change.old_attachment_names : change.old_value}
                            />{change.field_name === 'attachments'
                              ? <div className="vv-verified-attachment-to">to</div>
                              : ' to '}<AuditValue
                              fieldName={change.field_name}
                              fieldLabel={change.field_label}
                              value={change.field_name === 'attachments'
                                ? change.new_attachment_names : change.new_value}
                            />{change.field_name === 'attachments' ? null : '.'}
                          </>
                        ) : (
                          <>
                            <strong>{name}</strong> added {change.field_name === 'attachments' ? 'the ' : ''}
                            <strong>{getAuditFieldLabel(change, commissionAgreementAnswer)}</strong>{change.field_name === 'attachments' ? '.' : ': '}
                            <AuditValue
                              fieldName={change.field_name}
                              fieldLabel={change.field_label}
                              value={change.field_name === 'attachments'
                                ? change.new_attachment_names : change.new_value}
                            />{change.field_name === 'attachments' ? null : '.'}
                          </>
                        )}
                      </li>
                    );
                  })}
                  {verifierComment && (
                    <li className="vv-verified-change-item">
                      <strong>{verifyEntry?.verified_by?.name || 'Verifier'}</strong> left a comment:{' '}
                      &ldquo;{verifierComment}&rdquo;
                    </li>
                  )}
                </ul>
                );
              })()}
              <div className="vv-verified-next">
                <button
                  type="button"
                  className="vv-btn vv-btn--primary"
                  onClick={() => history.push(`/voucher/${voucherId}/review_and_send/send`)}
                >
                  Next
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* ── Two-column body ── */}
      <div className="vv-body">

        {/* ── Left Panel: Select Deal Info and Terms ── */}
        <div className="vv-panel vv-left-panel">
          <h3 className="vv-panel-title">Select Deal Info and Terms</h3>

          <p className="vv-field-list-caption">Deal Info and Terms</p>
          <div className="vv-field-list">
            {/* Required fields */}
            {defaultFields.map(field => (
              <div key={field.id} className="vv-field-item vv-field-required">
                <span className="vv-drag-handle vv-drag-handle--disabled" aria-hidden="true">
                  <DragHandleIcon color="#CAD0D8" />
                </span>
                <span className="vv-field-label">{field.label}</span>
              </div>
            ))}

            {/* Divider between required and optional */}
            <div className="vv-field-divider" />

            {/* Fixed optional fields — included by default and not reorderable */}
            {fixedOptionalFields.map(field => (
              <div key={field.id} className="vv-field-item vv-field-checked">
                <span className="vv-drag-handle vv-drag-handle--disabled" aria-hidden="true">
                  <DragHandleIcon color="#CAD0D8" />
                </span>
                <div
                  className="vv-optional-field-content"
                  onMouseDownCapture={handleLockedFieldMouseDown}
                  onClick={handleLockedFieldClick}
                >
                  <Checkbox
                    checked
                    onChange={() => {}}
                    id={`vv-field-${field.id}`}
                  >
                    {getFieldLabel(field)}
                  </Checkbox>
                </div>
              </div>
            ))}

            {/* OOB fields — validation failed: pre-checked, locked, non-reorderable */}
            {oobOptionalFields.map(field => (
              <div key={field.id} className="vv-field-item vv-field-checked">
                <span className="vv-drag-handle vv-drag-handle--disabled" aria-hidden="true">
                  <DragHandleIcon color="#CAD0D8" />
                </span>
                <div
                  className="vv-optional-field-content"
                  onMouseDownCapture={handleLockedFieldMouseDown}
                  onClick={handleLockedFieldClick}
                >
                  <Checkbox
                    checked
                    onChange={() => {}}
                    id={`vv-field-${field.id}`}
                  >
                    {getFieldLabel(field)}
                  </Checkbox>
                </div>
              </div>
            ))}

            {validOptionalFields.length > 0 && <div className="vv-field-divider" />}

            {/* Valid fields — validation passed: all reorderable, user can check/uncheck */}
            <SortableList className="vv-sortable-list" onDrop={handleOrderChange}>
              {validOptionalFields.map(field => (
                <div key={field.id} className="vv-optional-field-content">
                  <Checkbox
                    checked={field.checked}
                    onChange={() => toggleField(field.id)}
                    id={`vv-field-${field.id}`}
                  >
                    {getFieldLabel(field)}
                  </Checkbox>
                </div>
              ))}
            </SortableList>
          </div>
        </div>

        {/* ── Right Panel: Draft Email & Preview Form ── */}
        <div className="vv-panel vv-right-panel">
          <h3 className="vv-panel-title">Draft Email &amp; Preview Form</h3>

          {/* Email body + live preview — single card */}
          <div className="vv-draft-preview-container">
          <div className="vv-email-card">
            <span className="vv-email-card-label">Email body</span>
            <div className="vv-email-body-content">
              <textarea
                className="vv-email-textarea"
                value={emailBody}
                onChange={e => setEmailBody(e.target.value)}
                onBlur={e => saveEmailBody(e.target.value)}
              />
            </div>
          </div>

          {/* Live preview */}
          <div className="vv-preview-card">
            <div className="vv-preview-header">
              <h3 className="vv-preview-title">Voucher verification</h3>
              <span className="vv-preview-deal-link">{dealName}</span>
              {(propertyAddressLine || propertyCityStateLine) && (
                <div className="vv-preview-property-location">
                  {propertyAddressLine && <div>{propertyAddressLine}</div>}
                  {propertyCityStateLine && <div>{propertyCityStateLine}</div>}
                </div>
              )}
            </div>
            <table className="vv-preview-table">
              <tbody>
                {/* Preview preserves field position order */}
                {[...defaultFields, ...fixedOptionalFields, ...oobOptionalFields, ...validOptionalFields.filter(f => f.checked)].map(field => (
                  <tr key={field.id} className="vv-preview-row">
                    <td className="vv-preview-label">{getFieldLabel(field)}</td>
                    <td className="vv-preview-value">
                      {field.id === 'lease_escalation' && resolvedComp.rent_escalation_is_custom && resolvedComp.rent_escalation_custom?.length ? (
                        <div className="vv-escalation-scroll">
                          <table className="vv-escalation-table">
                            <thead>
                              <tr>
                                <th>#</th>
                                <th>Rent</th>
                                <th>Year</th>
                                <th>Month</th>
                              </tr>
                            </thead>
                            <tbody>
                              {resolvedComp.rent_escalation_custom.map((row, i) => {
                                const { rowNumber, value, years, months } = getEscalationRowData(row, i);
                                return (
                                  <tr key={`${rowNumber}-${value}-${years}-${months}`}>
                                    <td>{rowNumber}</td>
                                    <td>{value}</td>
                                    <td>{years}</td>
                                    <td>{months}</td>
                                  </tr>
                                );
                            })}
                            </tbody>
                          </table>
                        </div>
                      ) : (
                        <div className="vv-preview-value-text">{getPreviewValue(field.id)}</div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          </div>{/* end vv-draft-preview-container */}
        </div>
      </div>

      {/* ── Send Verification Email ── */}
      <div className="vv-send-section">
        <h2 className="vv-send-title">Send Verification Email</h2>

        {/* Autocomplete search — same engine as Send tab */}
        <div className="vv-send-to-fieldset">
          <span className="vv-send-to-legend">Send to</span>
          <div className="vv-send-autocomplete-wrap">
            <VoucherRecipientAutocomplete
              onSelect={addRecipient}
              filterResultsPredicate={filterSearchResults}
              placeholder="Search by name or email..."
              includeBothSources
            />
          </div>
        </div>

        {/* Lead producer — removable via ✕ icon */}
        {lpRecipient && (
          <div className="vv-recipient-row vv-recipient-row--lead">
            {renderRecipientAvatar(lpRecipient)}
            <div className="vv-recipient-info">
              <p className="vv-recipient-name">
                {lpRecipient.first_name} {lpRecipient.last_name}
                <span className="vv-recipient-role">&nbsp;(Lead Producer)</span>
              </p>
              <p className="vv-recipient-email">{lpRecipient.work_email}</p>
              <p className="vv-recipient-note">The producer receiving the highest commission must verify the deal terms</p>
            </div>
            <IconButton
              className="vv-recipient-remove"
              iconName="close-circle"
              onClick={() => {
                removeRecipient(lpRecipient);
                leadProducerSentRef.current = true;
              }}
              variant="basic"
              aria-label="Remove lead producer"
            />
          </div>
        )}
        {/* Removable recipients */}
        {displayedRecipients.filter(r => !r.broker_is_lead).map(r => (
          <div key={r.id} className="vv-recipient-row">
            {renderRecipientAvatar(r)}
            <div className="vv-recipient-info">
              <p className="vv-recipient-name">{r.first_name} {r.last_name}</p>
              <p className="vv-recipient-email">{r.work_email}</p>
            </div>
            <IconButton
              className="vv-recipient-remove"
              iconName="close-circle"
              onClick={() => removeRecipient(r)}
              variant="basic"
            />
          </div>
        ))}
      </div>

      {/* ── Bottom Action Bar ── */}
      <div className="vv-bottom-bar">
        <div className="vv-bottom-bar-left">
          <div
            className="vv-include-excel-wrapper"
            onMouseDownCapture={(event) => {
              includeExcelScrollRef.current = {
                x: window.scrollX,
                y: window.scrollY,
              };
              event.preventDefault();
            }}
            onClick={() => {
              const { x, y } = includeExcelScrollRef.current;
              const restoreScroll = () => window.scrollTo(x, y);
              window.requestAnimationFrame(() => {
                restoreScroll();
                window.setTimeout(restoreScroll, 0);
              });
            }}
          >
            <Checkbox
              checked={includeExcel}
              onChange={() => {
                const next = !includeExcel;
                setIncludeExcel(next);
                if (draftIdRef.current) {
                  vouchersService.updateVerificationExcelAttachment(draftIdRef.current, next).catch(() => {});
                }
              }}
              id="vv-include-excel"
            >
              Include this voucher&apos;s Excel file as an attachment to the verification email
            </Checkbox>
          </div>
        </div>
        <div className="vv-bottom-bar-right">
          <button
            type="button"
            className="vv-btn vv-btn--outline"
            onClick={() => history.push(`/voucher/${voucherId}/review_and_send/review`)}
          >
            Back
          </button>
          <button
            type="button"
            className="vv-btn vv-btn--outline"
            onClick={() => history.push(`/voucher/${voucherId}/review_and_send/send`)}
          >
            Skip This Step
          </button>
          {sendBlockReason ? (
            <Tooltip message={sendBlockReason} position="top" size="medium">
              <span>{sendButton}</span>
            </Tooltip>
          ) : sendButton}
        </div>
        {hasSentVerificationEmail && (
          <p className="vv-submission-details">
            Submitted by {submittedBy || 'Unknown user'} on {formatTimestamp(submittedDate)}
          </p>
        )}
      </div>
    </div>
  );
};

export default VoucherVerificationTab;
